import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { setAuthTokens, setStoredUser } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Mail, ArrowLeft } from 'lucide-react';
import logo from '@/assets/logo.png';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitted, setForgotSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Login and get tokens (normalize email casing to avoid mobile auto-capitalize issues)
      const response = await api.post('/auth/login', {
        ...formData,
        email: formData.email.trim().toLowerCase(),
      });
      
      // Store tokens
      setAuthTokens(response.data.access_token, response.data.refresh_token);

      // Get user info with the new token
      const userResponse = await api.get('/auth/me');
      
      // Store user
      setStoredUser(userResponse.data);

      toast.success('Login realizado com sucesso!');

      // Deep link (e.g. MCP consent screen) takes priority over role-based default
      const next = searchParams.get('next');
      if (next) {
        navigate(next);
        return;
      }

      // Redirect based on role
      const userRole = userResponse.data?.role?.toUpperCase();
      if (userRole === 'CLIENT') {
        navigate('/meus-projetos');
      } else {
        navigate('/home');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao fazer login. Verifique suas credenciais.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    if (forgotEmail) {
      setForgotSubmitted(true);
    }
  };

  const closeForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotEmail('');
    setForgotSubmitted(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <Card className="w-full max-w-md p-8 shadow-lg">
        <div className="text-center mb-8">
          <img src={logo} alt="Quatro Folhas - Soluções ambientais desde 1992" className="h-24 w-auto mx-auto mb-4" />
          <p className="text-gray-600">Sistema de Gestão de Projetos Ambientais</p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={loading}
              className="mt-1"
              autoCapitalize="none"
              autoCorrect="off"
              data-testid="email-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Senha</Label>
              <button
                type="button"
                onClick={() => setForgotPasswordOpen(true)}
                className="text-sm text-emerald-700 hover:text-emerald-800 hover:underline"
                data-testid="forgot-password-link"
              >
                Esqueci minha senha
              </button>
            </div>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              disabled={loading}
              className="mt-1"
              data-testid="password-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-emerald-800 hover:bg-emerald-900 text-white h-12 text-base font-medium"
            disabled={loading}
            data-testid="login-button"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Entrando...
              </div>
            ) : (
              'Entrar'
            )}
          </Button>

          <div className="text-xs text-center text-gray-500">
            Ao fazer login, você concorda com nossos{' '}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                navigate('/terms');
              }}
              className="text-emerald-700 hover:text-emerald-800 underline"
            >
              Termos de Uso
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Não tem acesso? Entre em contato com o administrador.
          </p>
        </div>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={closeForgotPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Recuperar Senha
            </DialogTitle>
          </DialogHeader>

          {!forgotSubmitted ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-gray-600">
                Informe seu email cadastrado para solicitar a recuperação de senha.
              </p>
              
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="seu@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  className="mt-1"
                  data-testid="forgot-email-input"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <Button type="button" variant="outline" onClick={closeForgotPassword}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                  Solicitar
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-emerald-800">Solicitação Recebida</h4>
                    <p className="text-sm text-emerald-700 mt-1">
                      Para redefinir sua senha, entre em contato com o administrador:
                    </p>
                    <a 
                      href="mailto:quatrofolhas.f@gmail.com?subject=Solicitação de Recuperação de Senha&body=Olá, gostaria de solicitar a recuperação da senha para o email: ${forgotEmail}"
                      className="inline-flex items-center gap-2 mt-3 text-emerald-800 font-medium hover:underline"
                    >
                      <Mail className="w-4 h-4" />
                      quatrofolhas.f@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600">
                Informe seu email cadastrado (<strong>{forgotEmail}</strong>) na mensagem para que o administrador possa redefinir sua senha.
              </p>

              <div className="flex justify-end pt-2">
                <Button onClick={closeForgotPassword} className="bg-emerald-800 hover:bg-emerald-900">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar ao Login
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Login;
